"use client";

import { ArrowLeft, Clock, CheckCircle, HelpCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Solution() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const faqs = [{
    question: "תוך כמה זמן ניתן למצוא מטפל סיעודי?",
    answer: "השירות שלנו מתמחה במהירות שיא. אנו מתחייבים לקשר אתכם אל מטפל סיעודי מקצועי תוך 5 שעות בלבד מרגע הפנייה, כדי להבטיח רצף טיפולי מלא."
  }, {
    question: "מה היתרון של מטפל סיעודי פרטי על פני חברות כוח אדם?",
    answer: "בחירה ב-מטפל סיעודי פרטי דרך Siudy מבטיחה לכם יחס אישי, סינון קפדני של המטפלים וזמינות מיידית ללא בירוקרטיה מיותרת."
  }, {
    question: "האם השירות של מטפל סיעודי בבית זמין גם בסופי שבוע?",
    answer: "בהחלט. אנו מספקים מטפל סיעודי בבית 24/7, כולל שבתות וחגים, בכל רחבי הארץ ובדגש על אזור המרכז."
  }, {
    question: "האם המטפלים שלכם הם ישראלים?",
    answer: "כן, אנו עובדים אך ורק עם מטפלים ישראלים דוברי עברית, כדי להבטיח תקשורת מיטבית והבנה מלאה של צרכי המטופל."
  }, {
    question: "מה קורה במקרה של חוסר התאמה עם המטפל?",
    answer: "שביעות הרצון שלכם היא בראש סדר העדיפויות שלנו. במקרה של חוסר התאמה, אנו נדאג להחלפת המטפל במהירות המרבית כדי לשמור על רצף הטיפול."
  }, {
    question: "האם ניתן להזמין שירות להשגחה בבית חולים?",
    answer: "בוודאי. המטפלים שלנו זמינים למתן שירותי השגחה וליווי צמוד גם בבתי חולים, כדי להקל על המשפחה ולהבטיח שהמטופל לעולם לא יישאר לבד."
  }];
  return <section id="פתרון" className="py-24 bg-gradient-to-b from-background to-muted/30 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-ring/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <p className="text-xl md:text-2xl text-muted-foreground font-default leading-relaxed">
              כשמדובר בבריאות המשפחה, מציאת <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי</Link> אמין היא בראש סדר העדיפויות. 
              אצלנו תקבלו מענה מיידי המותאם בדיוק לצרכים שלכם, כולל <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי בתל אביב</Link> עם אפשרות לשיבוץ <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי בבית</Link> תוך שעות ספורות בלבד.
            </p>
          </div>

          <Card className="max-w-4xl mx-auto rounded-[16px] shadow-[0_8px_30px_hsl(var(--foreground)/0.05)] bg-card/90 backdrop-blur-md border-border/50">
            <CardHeader className="text-center pt-12 pb-6">
              <h2 className="text-3xl md:text-5xl font-bold font-default text-ring tracking-tight">
                הפתרון המיידי שלנו
              </h2>
            </CardHeader>
            <CardContent className="flex flex-col items-center px-6 md:px-16">
              <div className="relative flex items-center justify-center mb-12 mt-4">
                <div className="absolute inset-0 bg-chart-2/10 rounded-full blur-2xl animate-pulse" />
                <div className="relative flex flex-col items-center justify-center w-48 h-48 rounded-full border-4 border-chart-2 bg-card shadow-xl z-10">
                  <Clock className="w-10 h-10 text-chart-2 mb-2" />
                  <span className="text-6xl font-bold font-default text-chart-2 leading-none">
                    05
                  </span>
                  <span className="text-lg font-medium text-chart-2/80 mt-1 uppercase tracking-widest font-default">
                    שעות
                  </span>
                </div>
              </div>

              <div className="space-y-12 w-full text-right">
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-foreground">הצורך במענה מהיר ומקצועי</h3>
                  <p className="text-lg text-muted-foreground font-default leading-relaxed">
                    מערכת Siudy הוקמה כדי לתת מענה למצבי חירום וצורך מיידי בליווי רפואי וסיעודי. אנו מבינים שכל דקה קריטית, ולכן פיתחנו מודל המאפשר גיוס <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי</Link> מקצועי ומנוסה תוך 5 שעות בלבד. השירות שלנו מבטיח שכל <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי בבית</Link> עובר תהליך מיון קפדני. אנו מספקים שירותי <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי בתל אביב</Link>, <Link to="#" className="text-ring font-semibold hover:underline">השגחה בבית חולים</Link> וכן <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי בירושלים</Link> הכוללים בדיקת רקע, המלצות והכשרה מתאימה המותאמת לסטנדרטים הישראליים הגבוהים ביותר.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-foreground">איכות וביטחון למשפחה</h3>
                  <p className="text-lg text-muted-foreground font-default leading-relaxed">
                    הבחירה ב-<Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי פרטי</Link> מאפשרת לכם לקבל שירות המותאם אישית לאופי המטופל ולדרישות הספציפיות של המקרה. אנו מתמחים במתן פתרונות של <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי בבית</Link> וכן <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי פרטי</Link> תוך דגש על יחס אנושי, חמלה ומקצוענות קלינית. תוכלו לקרוא עוד על הסטנדרטים שלנו ועל זכויות המטופל ב<Link to="#" className="text-ring font-semibold hover:underline">בלוג המקצועי</Link> שלנו, המעודכן באופן שוטף, שם תוכלו למצוא מידע על <Link to="#" className="text-ring font-semibold hover:underline">היתרונות של מטפל סיעודי</Link>.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-foreground">איך אנחנו מבטיחים זמינות?</h3>
                  <p className="text-lg text-muted-foreground font-default leading-relaxed">
                    הטכנולוגיה שפיתחנו מאפשרת לנו לאתר את המטפלים הקרובים ביותר אליכם שנמצאים בסטטוס "זמין מיידית". זהו הפתרון האופטימלי למי שמחפש שירות מהיר ללא פשרות על איכות. אנו מלווים אתכם מרגע הפנייה ועד לשיבוץ המוצלח. אם אתם זקוקים לעזרה עכשיו, פשוט <Link to="#" className="text-ring font-semibold hover:underline">צרו קשר</Link> או הזמינו <Link to="#" className="text-ring font-semibold hover:underline">השגחה בבית חולים</Link> והצוות המקצועי שלנו יטפל בכל הפרטים עבורכם.
                  </p>
                </div>

                <div className="bg-muted/30 rounded-2xl p-8 border border-border/50">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-6 h-6 text-chart-2" />
                    <h3 className="text-xl font-bold text-foreground">יתרונות השירות</h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    היתרונות המרכזיים בבחירת <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי בבית</Link> או <Link to="#" className="text-ring font-semibold hover:underline">השגחה בבית חולים</Link> דרך הפלטפורמה שלנו כוללים זמינות מלאה 24/7, התאמה אישית של <Link to="#" className="text-ring font-semibold hover:underline">מטפל סיעודי פרטי</Link> לפי שפה ואופי המטופל, ופיקוח שוטף על איכות הטיפול המוענק ליקיריכם בכל רגע נתון.
                  </p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col sm:flex-row justify-center gap-4 pb-14 pt-8">
              <Button asChild size="lg" className="rounded-full bg-gradient-to-r from-ring to-chart-4 text-primary-foreground hover:opacity-90 shadow-[0_0_20px_hsl(var(--ring)/0.4)] px-12 py-8 text-xl font-semibold font-default transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_hsl(var(--ring)/0.6)] group border-0">
                <Link to="#">
                  התחל עכשיו
                  <ArrowLeft className="mr-3 w-6 h-6 transition-transform group-hover:-translate-x-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full border-ring text-ring hover:bg-ring/10 px-12 py-8 text-xl font-semibold font-default transition-all duration-300 hover:scale-105">
                <Link to="#">
                  ייעוץ טלפוני חינם
                  <Phone className="mr-3 w-6 h-6" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          <div className="mt-20 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-10">
              <HelpCircle className="w-8 h-8 text-ring" />
              <h2 className="text-3xl font-bold text-foreground">שאלות נפוצות</h2>
            </div>
            <div className="grid gap-4">
              {faqs.map((faq, i) => <Card key={i} data-index={i} className="border-border/50 bg-card/50 backdrop-blur-sm">
                  <CardHeader className="py-4">
                    <CardTitle className="text-lg font-bold text-ring">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent className="pb-6">
                    <p className="text-muted-foreground leading-relaxed">
                      {faq.answer}
                      {i === 0 && <>
                          {" "}
                          ניתן להזמין{" "}
                          <Link to="#" className="text-ring font-semibold hover:underline">
                            מטפל סיעודי
                          </Link>{" "}
                          או{" "}
                          <Link to="#" className="text-ring font-semibold hover:underline">
                            השגחה בבית חולים
                          </Link>{" "}
                          באופן מיידי.
                        </>}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}